/**
 * Category tap effects (owner, 2026-10-30; "option 1" from the chat mockups,
 * tried on the local /category-tap-fx-lab first).
 *
 * A category icon is one flat picture, so nothing inside it can move on its
 * own. Each category instead gets a short effect drawn on top of its icon:
 * ripples and droplets for pools, a wave for boats, steam and petals for
 * spas, a cloche lift for restaurants, and so on. Keyframes live in
 * lib/tailwind.css (mjfx-*).
 *
 * Tapping a category opens a new page, so an effect started on the tap would
 * be cut off. The tap only queues it (queueCategoryFx); the hub tab rail
 * plays it on the tab of the category the guest lands on
 * (useQueuedCategoryFx). That also carries a tap on the home page's
 * "Pick your vibe" chips over to the category page.
 *
 * The particles are drawn on a fixed 72px layer centred on the icon and
 * scaled to its real size, so the same numbers work for a 36px tab icon and
 * a 72px tile. Reduced motion plays nothing.
 */
import { useEffect, type RefObject } from "react";

type Fx = (stage: HTMLElement, icon: HTMLElement) => void;

/** The size the effect numbers were drawn for. */
const DESIGN_PX = 72;

/** Spawns one particle, lets its keyframe run, then removes it. */
function spawn(stage: HTMLElement, style: Record<string, string | number>, ms: number, text?: string) {
  const layer = stage.querySelector<HTMLElement>(":scope > .mjfx-layer");
  if (!layer) return;
  const el = document.createElement("span");
  el.className = "mjfx-p";
  if (text) el.textContent = text;
  for (const [k, v] of Object.entries(style)) {
    if (k.startsWith("--")) el.style.setProperty(k, String(v));
    else (el.style as unknown as Record<string, string>)[k] = String(v);
  }
  layer.appendChild(el);
  window.setTimeout(() => el.remove(), ms);
}

/** Restarts a keyframe on the icon even if the same one just played. */
function replay(el: HTMLElement, animation: string) {
  el.style.animation = "none";
  void el.offsetWidth;
  el.style.animation = animation;
}

const FX: Record<string, Fx> = {
  pools(stage, icon) {
    replay(icon, "mjfx-squish 600ms ease-out");
    for (let i = 0; i < 3; i++) {
      spawn(stage, {
        left: "48%", top: "58%", width: "75%", height: "30%",
        border: "2px solid #7cc7f2", borderRadius: "50%",
        animation: `mjfx-ripple 900ms ease-out ${i * 180}ms both`,
      }, 1400);
    }
    [[-16, -30], [2, -40], [18, -28], [-6, -22]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "46%", top: "46%", width: "10%", height: "12%",
        borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%", background: "#5fb6ea",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-fly 650ms cubic-bezier(.2,.7,.4,1) ${80 + i * 40}ms both`,
      }, 1000),
    );
  },
  boats(stage, icon) {
    replay(icon, "mjfx-rock 1100ms ease-in-out");
    for (let i = 0; i < 2; i++) {
      spawn(stage, {
        left: "8%", bottom: `${4 + i * 7}%`, width: "84%", height: "8%",
        borderTop: "2.5px solid #5fb6ea", borderRadius: "50%",
        animation: `mjfx-wave 1000ms ease-in-out ${i * 220}ms both`,
      }, 1400);
    }
    [[-14, -18], [14, -20]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "46%", bottom: "14%", width: "8%", height: "8%", borderRadius: "50%", background: "#9fd6f5",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-fly 600ms ease-out ${300 + i * 60}ms both`,
      }, 1100),
    );
  },
  spas(stage, icon) {
    replay(icon, "mjfx-breathe 1400ms ease-in-out");
    [-10, 0, 10].forEach((dx, i) =>
      spawn(stage, {
        left: `${42 + dx / 2}%`, top: "18%", width: "20%", height: "20%", borderRadius: "50%", background: "#e9e4f2",
        "--dx": `${dx}px`,
        animation: `mjfx-steam 1500ms ease-out ${i * 250}ms both`,
      }, 2100),
    );
    // Two lotus petals lift off the flower at the top and drift away.
    [[-24, -26], [22, -30]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "50%", top: "22%", width: "12%", height: "8%", borderRadius: "50% 0", background: "#f6a3c0",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-petal 1300ms ease-out ${300 + i * 200}ms both`,
      }, 1900),
    );
  },
  activities(stage, icon) {
    replay(icon, "mjfx-rev 800ms cubic-bezier(.3,.8,.4,1)");
    [[-22, -6], [-28, -14], [-18, 4]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "18%", bottom: "14%", width: "16%", height: "16%", borderRadius: "50%", background: "#d9c3a2",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-dust 700ms ease-out ${120 + i * 70}ms both`,
      }, 1100),
    );
  },
  restaurants(stage, icon) {
    replay(icon, "mjfx-cloche 1000ms cubic-bezier(.3,.8,.4,1)");
    // Steam escaping from under the lifted edge.
    [-12, 0, 12].forEach((dx, i) =>
      spawn(stage, {
        left: `${40 + dx / 2}%`, top: "52%", width: "18%", height: "18%", borderRadius: "50%", background: "#efe9df",
        "--dx": `${dx}px`,
        animation: `mjfx-steam 1200ms ease-out ${220 + i * 120}ms both`,
      }, 1800),
    );
    // A glint that sweeps across the dome, clipped to the icon's own shape so
    // it never paints outside the gold.
    // The icon may be the <img> itself or a wrapper around it (the hero
    // sticker keeps its tilt on the image and lets the effect move the wrapper).
    const src = icon instanceof HTMLImageElement ? icon.src : (icon.querySelector("img")?.src ?? "");
    spawn(stage, {
      inset: "0",
      background: "linear-gradient(100deg, transparent 35%, rgba(255,255,255,.9) 50%, transparent 65%) no-repeat",
      backgroundSize: "250% 100%",
      maskImage: `url("${src}")`, WebkitMaskImage: `url("${src}")`,
      maskSize: "contain", WebkitMaskSize: "contain",
      maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat",
      maskPosition: "center", WebkitMaskPosition: "center",
      animation: "mjfx-glint 700ms ease-in-out 800ms both",
    }, 1700);
  },
  excursions(stage, icon) {
    replay(icon, "mjfx-bumps 900ms ease-out");
    [[-22, -8], [22, -8], [-14, -16], [14, -16]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "42%", bottom: "12%", width: "16%", height: "16%", borderRadius: "50%", background: "#d9c3a2",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-dust 650ms ease-out ${i < 2 ? 150 : 480}ms both`,
      }, 1200),
    );
  },
  stays(stage, icon) {
    replay(icon, "mjfx-sink 900ms ease-out");
    // The courtyard fountain sits low in the middle of the riad.
    [[-8, -22], [0, -30], [8, -22], [-3, -26], [4, -18]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "47%", top: "66%", width: "6%", height: "7%",
        borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%", background: "#5fb6ea",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-fly 600ms ease-out ${300 + i * 70}ms both`,
      }, 1300),
    );
  },
  nightclubs(stage, icon) {
    replay(icon, "mjfx-disco 1100ms ease-in-out");
    const colours = ["#ff5fa2", "#5fd0ff", "#ffd45f", "#9b7bff", "#5fffb0", "#ff8a5f"];
    colours.forEach((bg, i) => {
      const angle = (i / colours.length) * Math.PI * 2;
      spawn(stage, {
        left: "46%", top: "46%", width: "8%", height: "8%", borderRadius: "2px", background: bg,
        "--dx": `${Math.round(Math.cos(angle) * 34)}px`, "--dy": `${Math.round(Math.sin(angle) * 30)}px`,
        animation: `mjfx-fly 800ms ease-out ${150 + i * 60}ms both`,
      }, 1400);
    });
  },
  trips(stage, icon) {
    replay(icon, "mjfx-hop 800ms cubic-bezier(.3,.8,.4,1)");
    [[-24, -18], [24, -16], [0, -32]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "46%", top: "40%", width: "9%", height: "9%", background: "#E5B265",
        clipPath: "polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%)",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-fly 700ms ease-out ${250 + i * 60}ms both`,
      }, 1100),
    );
  },
  workshops(stage, icon) {
    replay(icon, "mjfx-wheel 900ms cubic-bezier(.3,.8,.4,1)");
    [[-26, -4], [26, -6], [-20, -16], [20, -14]].forEach(([dx, dy], i) =>
      spawn(stage, {
        left: "46%", top: "50%", width: "7%", height: "7%", borderRadius: "50%", background: "#b8673a",
        "--dx": `${dx}px`, "--dy": `${dy}px`,
        animation: `mjfx-fly 550ms ease-out ${200 + i * 90}ms both`,
      }, 1100),
    );
  },
};

/** Whether a category has an effect. */
export function hasCategoryFx(slug: string | undefined): boolean {
  return !!slug && slug in FX;
}

/**
 * Plays a category's effect on `stage`: a positioned element holding the icon
 * as an <img class="mjfx-icon">.
 */
export function playCategoryFx(slug: string, stage: HTMLElement | null) {
  if (!stage || !(slug in FX)) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const icon = stage.querySelector<HTMLElement>(".mjfx-icon");
  if (!icon) return;
  let layer = stage.querySelector<HTMLElement>(":scope > .mjfx-layer");
  if (!layer) {
    layer = document.createElement("span");
    layer.className = "mjfx-layer";
    layer.setAttribute("aria-hidden", "true");
    stage.appendChild(layer);
  }
  layer.style.transform = `translate(-50%, -50%) scale(${(stage.offsetWidth || DESIGN_PX) / DESIGN_PX})`;
  FX[slug](stage, icon);
}

/** Set by a tap that is about to navigate, read once by the page it opens. */
let queued: { slug: string; at: number } | null = null;

/** Call from the tap that opens a category page. */
export function queueCategoryFx(slug: string) {
  queued = hasCategoryFx(slug) ? { slug, at: Date.now() } : null;
}

/**
 * Plays the queued effect once `slug` is the active category and its stage
 * is on screen. A queue older than a few seconds is dropped, so a slow page
 * or a Back press never fires a stale splash.
 */
export function useQueuedCategoryFx(slug: string | undefined, stage: RefObject<HTMLElement | null>, enabled = true) {
  useEffect(() => {
    if (!enabled || !slug || !queued) return;
    if (queued.slug !== slug || Date.now() - queued.at > 4000) return;
    // Let the tab rail lay out and centre before the effect starts. The
    // queue is only used up when the effect actually plays: going from "All"
    // to a category mounts the rail twice, and the first mount's timer is
    // cancelled by the second.
    const id = window.setTimeout(() => {
      if (queued?.slug !== slug) return;
      queued = null;
      playCategoryFx(slug, stage.current);
    }, 120);
    return () => window.clearTimeout(id);
  }, [slug, stage, enabled]);
}
